import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import guardAuthService from '../../services/guardAuth.service';

export default function ChangePassword() {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const validateNewPassword = (pwd: string): string | null => {
    if (pwd.length < 10) return 'Password must be at least 10 characters.';
    if (!/[A-Za-z]/.test(pwd)) return 'Password must contain at least one letter.';
    if (!/\d/.test(pwd)) return 'Password must contain at least one digit.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }

    const policyError = validateNewPassword(newPassword);
    if (policyError) {
      setError(policyError);
      return;
    }

    setSubmitting(true);
    try {
      await guardAuthService.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      navigate('/yard/inventory', { replace: true });
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to change password.';
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-4 pb-8">
      <div className="max-w-md mx-auto mt-4 bg-panel rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="px-5 py-4 border-b">
          <h1 className="text-lg font-semibold">Change Password</h1>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Current Password
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded px-3 py-2 text-sm"
              required
              disabled={submitting}
              autoComplete="current-password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded px-3 py-2 text-sm"
              required
              disabled={submitting}
              autoComplete="new-password"
            />
            <p className="mt-1 text-xs text-muted">
              Must be at least 10 characters and contain a letter and a digit.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded px-3 py-2 text-sm"
              required
              disabled={submitting}
              autoComplete="new-password"
            />
          </div>

          {error && (
            <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {submitting ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
